// Google Identity Services authorization code client wrapper.
//
// Responsibilities:
//   - Lazy-load the GSI script.
//   - Exchange one-time authorization codes through the auth function.
//   - Persist access tokens, expiry and the sealed refresh token.
//   - Refresh access tokens through the auth function without opening GIS.
//   - Fetch the signed-in user's email through the Drive About endpoint.

import { NetworkError, TokenExpiredError } from "../../errors";

import {
  DRIVE_API,
  FOLDER_STORAGE_KEY,
  GSI_SRC,
  REFRESH_LEAD_MS,
  SCOPES,
  TOKEN_STORAGE_KEY,
} from "./constants";

import type { AuthState, CloudAccount } from "../../types";

type TokenEnvelope = {
  accessToken: string;
  /** Epoch ms. */
  expiresAt: number;
  account: CloudAccount;
  sealedRefreshToken?: string;
};

type AuthTokenResponse = {
  accessToken: string;
  expiresIn: number;
  sealedRefreshToken?: string | null;
};

type GsiCodeResponse = {
  code?: string;
  error?: string;
  error_description?: string;
};

type GsiCodeClient = {
  requestCode: () => void;
};

type GsiGlobal = {
  accounts: {
    oauth2: {
      initCodeClient: (cfg: {
        client_id: string;
        scope: string;
        ux_mode: "popup";
        callback: (resp: GsiCodeResponse) => void;
        error_callback?: (err: { type: string; message?: string }) => void;
      }) => GsiCodeClient;
      revoke: (token: string, done?: () => void) => void;
    };
  };
};

/** sessionStorage key the pre-localStorage build used for the token. */
const LEGACY_TOKEN_STORAGE_KEY = "drawOla_gdrive_token_v1";

/** localStorage key used by the access-token-only format. */
const LEGACY_LOCAL_TOKEN_STORAGE_KEY = "drawOla_gdrive_token_v2";

/** How long one code request may wait before it is treated as failed. GIS
 *  calls neither callback when the popup dies with the tab, so without this an
 *  interactive request could stay pending for the whole session. */
const REQUEST_TIMEOUT_MS = 30000;

let scriptPromise: Promise<void> | null = null;

function loadGsi(): Promise<void> {
  if (scriptPromise) {
    return scriptPromise;
  }
  scriptPromise = new Promise((resolve, reject) => {
    if ((window as any).google?.accounts?.oauth2) {
      resolve();
      return;
    }
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${GSI_SRC}"]`,
    );
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener(
        "error",
        () => reject(new Error("Failed to load GSI script")),
        { once: true },
      );
      return;
    }
    const s = document.createElement("script");
    s.src = GSI_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Failed to load GSI script"));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

function loadToken(): TokenEnvelope | null {
  try {
    // Legacy tokens cannot refresh; clear both old formats once.
    sessionStorage.removeItem(LEGACY_TOKEN_STORAGE_KEY);
    localStorage.removeItem(LEGACY_LOCAL_TOKEN_STORAGE_KEY);
    const raw = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as TokenEnvelope;
    if (!parsed.accessToken || !parsed.expiresAt || !parsed.account) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function saveToken(env: TokenEnvelope) {
  try {
    localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(env));
  } catch {
    // localStorage may be disabled; ignore
  }
}

function clearToken() {
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(FOLDER_STORAGE_KEY);
  } catch {
    // ignore
  }
}

async function fetchUserInfo(accessToken: string): Promise<CloudAccount> {
  const res = await fetch(
    `${DRIVE_API}/about?fields=user(emailAddress,displayName,photoLink)`,
    { headers: { Authorization: `Bearer ${accessToken}` } },
  );
  if (!res.ok) {
    throw new Error(`userinfo failed: ${res.status}`);
  }
  const data = (await res.json()) as {
    user?: {
      emailAddress?: string;
      displayName?: string;
      photoLink?: string;
    };
  };
  return {
    email: data.user?.emailAddress ?? "unknown@google",
    displayName: data.user?.displayName,
    avatarUrl: data.user?.photoLink,
  };
}

export type DriveAuth = {
  init(): Promise<void>;
  authenticate(opts?: { interactive?: boolean }): Promise<CloudAccount>;
  signOut(): Promise<void>;
  getAuthState(): AuthState;
  subscribeAuth(listener: (s: AuthState) => void): () => void;
  /** Returns a valid access token or throws TokenExpiredError or NetworkError. */
  getAccessToken(opts?: { forceRefresh?: boolean }): Promise<string>;
};

/** Creates the Google Drive authorization client. */
export function createDriveAuth(clientId: string, authUrl: string): DriveAuth {
  let codeClient: GsiCodeClient | null = null;
  let current: TokenEnvelope | null = loadToken();
  let refreshTimer: number | null = null;
  let pendingCallback: ((response: GsiCodeResponse) => void) | null = null;
  let pendingReject: ((e: Error) => void) | null = null;
  let interactiveInFlight: Promise<CloudAccount> | null = null;
  let refreshInFlight: Promise<string> | null = null;
  const listeners = new Set<(s: AuthState) => void>();

  const authState = (): AuthState => {
    if (!current) {
      return { status: "signed-out" };
    }
    return { status: "signed-in", account: current.account };
  };

  const emit = (s: AuthState) => {
    for (const l of listeners) {
      try {
        l(s);
      } catch {
        // ignore
      }
    }
  };

  const clearRefresh = () => {
    if (refreshTimer !== null) {
      window.clearTimeout(refreshTimer);
      refreshTimer = null;
    }
  };

  const clearSession = (): void => {
    clearRefresh();
    current = null;
    clearToken();
    emit(authState());
  };

  /** Hand a failure GSI reported outside `callback` to whoever is waiting. */
  const failPending = (e: Error) => {
    pendingReject?.(e);
  };

  const ensureClient = async (): Promise<GsiCodeClient> => {
    await loadGsi();
    const g = (window as any).google as GsiGlobal | undefined;
    if (!g?.accounts?.oauth2?.initCodeClient) {
      throw new Error("GSI not available");
    }
    if (codeClient) {
      return codeClient;
    }
    codeClient = g.accounts.oauth2.initCodeClient({
      client_id: clientId,
      scope: SCOPES.join(" "),
      ux_mode: "popup",
      callback: (resp) => {
        pendingCallback?.(resp);
      },
      // A popup that is blocked or closed is reported here and `callback`
      // never runs, so without this the request would never settle.
      error_callback: (err) => {
        failPending(
          new Error(err?.message ?? err?.type ?? "code request failed"),
        );
      },
    });
    return codeClient;
  };

  const requestCode = (): Promise<string> =>
    new Promise<string>((resolve, reject) => {
      let timer: ReturnType<typeof setTimeout> | undefined;
      const settle = () => {
        if (timer !== undefined) {
          clearTimeout(timer);
          timer = undefined;
        }
        pendingCallback = null;
        pendingReject = null;
      };
      pendingCallback = (resp) => {
        settle();
        if (resp.error || !resp.code) {
          reject(
            new Error(
              resp.error_description ?? resp.error ?? "code request failed",
            ),
          );
          return;
        }
        resolve(resp.code);
      };
      pendingReject = (e) => {
        settle();
        reject(e);
      };
      ensureClient()
        .then((client) => {
          timer = setTimeout(() => {
            settle();
            reject(new Error("code request timed out"));
          }, REQUEST_TIMEOUT_MS);
          client.requestCode();
        })
        .catch((e) => {
          settle();
          reject(e);
        });
    });

  const consume = async (resp: AuthTokenResponse): Promise<CloudAccount> => {
    const account = current?.account ?? (await fetchUserInfo(resp.accessToken));
    current = {
      accessToken: resp.accessToken,
      expiresAt: Date.now() + resp.expiresIn * 1000,
      account,
      sealedRefreshToken:
        resp.sealedRefreshToken ?? current?.sealedRefreshToken,
    };
    saveToken(current);
    scheduleRefresh();
    emit(authState());
    return account;
  };

  const readJsonResponse = async (res: Response): Promise<unknown> => {
    let body: unknown;
    try {
      body = await res.json();
    } catch (e) {
      throw new NetworkError(e);
    }
    return body;
  };

  const parseTokenResponse = (body: unknown): AuthTokenResponse => {
    if (
      !body ||
      typeof body !== "object" ||
      typeof (body as AuthTokenResponse).accessToken !== "string" ||
      typeof (body as AuthTokenResponse).expiresIn !== "number"
    ) {
      throw new NetworkError();
    }
    return body as AuthTokenResponse;
  };

  const exchangeCode = async (code: string): Promise<CloudAccount> => {
    let res: Response;
    try {
      res = await fetch(`${authUrl}/exchange`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      });
    } catch (e) {
      throw new NetworkError(e);
    }
    if (!res.ok) {
      throw new Error(`code exchange failed: ${res.status}`);
    }
    return consume(parseTokenResponse(await readJsonResponse(res)));
  };

  const performSilentRefresh = async (): Promise<string> => {
    const sealedRefreshToken = current?.sealedRefreshToken;
    if (!sealedRefreshToken) {
      throw new Error("no refresh token");
    }
    let res: Response;
    try {
      res = await fetch(`${authUrl}/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sealedRefreshToken }),
      });
    } catch (e) {
      throw new NetworkError(e);
    }
    const body = await readJsonResponse(res);
    if (res.status === 400 || res.status === 401) {
      clearSession();
      throw new TokenExpiredError();
    }
    if (!res.ok) {
      throw new NetworkError();
    }
    await consume(parseTokenResponse(body));
    return current!.accessToken;
  };

  const silentRefresh = (): Promise<string> => {
    if (refreshInFlight) {
      return refreshInFlight;
    }
    const promise = performSilentRefresh();
    refreshInFlight = promise;
    const finish = () => {
      if (refreshInFlight === promise) {
        refreshInFlight = null;
      }
    };
    promise.then(finish, finish);
    return promise;
  };

  const scheduleRefresh = () => {
    clearRefresh();
    if (!current?.sealedRefreshToken) {
      return;
    }
    const delay = Math.max(0, current.expiresAt - Date.now() - REFRESH_LEAD_MS);
    refreshTimer = window.setTimeout(() => {
      silentRefresh().catch(() => undefined);
    }, delay);
  };

  const authenticateInteractively = (): Promise<CloudAccount> => {
    if (interactiveInFlight) {
      return interactiveInFlight;
    }
    emit({ status: "signing-in" });
    const promise = requestCode()
      .then(exchangeCode)
      .catch((error) => {
        emit({ status: "error", error: String(error?.message ?? error) });
        throw error;
      });
    interactiveInFlight = promise;
    const finish = () => {
      if (interactiveInFlight === promise) {
        interactiveInFlight = null;
      }
    };
    promise.then(finish, finish);
    return promise;
  };

  const authenticateSilently = async (): Promise<CloudAccount> => {
    if (!current?.sealedRefreshToken) {
      emit(authState());
      throw new Error("no refresh token");
    }
    emit({ status: "signing-in" });
    try {
      await silentRefresh();
      return current!.account;
    } catch (error) {
      emit(authState());
      throw error;
    }
  };

  return {
    async init() {
      await loadGsi();
      await ensureClient();
      if (current) {
        if (current.expiresAt <= Date.now() + REFRESH_LEAD_MS) {
          if (current.sealedRefreshToken) {
            scheduleRefresh();
          } else {
            clearToken();
            current = null;
          }
        } else {
          scheduleRefresh();
        }
      }
      emit(authState());
    },

    authenticate(opts) {
      if (opts?.interactive === false) {
        return authenticateSilently();
      }
      if (current && current.expiresAt > Date.now() + REFRESH_LEAD_MS) {
        return Promise.resolve(current.account);
      }
      return authenticateInteractively();
    },

    async signOut() {
      clearRefresh();
      const token = current?.accessToken;
      current = null;
      clearToken();
      if (token) {
        try {
          await loadGsi();
          const g = (window as any).google as GsiGlobal | undefined;
          g?.accounts.oauth2.revoke(token);
        } catch {
          // best effort
        }
      }
      emit(authState());
    },

    getAuthState() {
      return authState();
    },

    subscribeAuth(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    async getAccessToken(opts) {
      if (!current) {
        throw new TokenExpiredError();
      }
      if (
        opts?.forceRefresh ||
        current.expiresAt <= Date.now() + REFRESH_LEAD_MS
      ) {
        if (!current.sealedRefreshToken) {
          clearSession();
          throw new TokenExpiredError();
        }
        try {
          await silentRefresh();
        } catch (error) {
          if (
            error instanceof NetworkError &&
            current?.expiresAt > Date.now()
          ) {
            return current.accessToken;
          }
          throw error;
        }
      }
      return current.accessToken;
    },
  };
}
